import { Text, Badge, Stack, Group } from "@mantine/core"
import type { CertificateStatus } from "@tfg/db"
import { CopyButton, DocumentActionsCell } from "@tfg/ui"
import type { TFunction } from "i18next"
import { useTranslation } from "react-i18next"

import { FindCertificateByIdResponseDto } from "~/gen/api"
import { fetchDocumentUrl } from "~/hooks/useDocumentsQuery"
import { useAuth } from "~/store/auth-context"

interface CertificateViewerProps {
  certificate: Pick<
    FindCertificateByIdResponseDto,
    "id" | "type" | "reference" | "status" | "documentId"
  >
}

export const CertificateViewer = ({ certificate }: CertificateViewerProps) => {
  const { t } = useTranslation(["traceability", "common"])
  const { isAdmin } = useAuth()

  const badgeColors: { [K in CertificateStatus]: string } = {
    NOT_UPLOADED: "gray",
    REJECTED: "yellow",
    PENDING: "blue",
    PROCESSED: "blue",
  } as const

  const badgeStatus: { [K in CertificateStatus]: ReturnType<TFunction> } = {
    NOT_UPLOADED: t("traceability:status.NOT_UPLOADED"),
    REJECTED: t("traceability:status.REJECTED"),
    PENDING: t("traceability:status.UPLOADED"),
    PROCESSED: t("traceability:status.PROCESSED"),
  } as const

  return (
    <Stack gap="var(--mantine-spacing-xxs)">
      {isAdmin && (
        <CopyButton
          label="Certificate Id"
          size="xs"
          copyableText={certificate.id}
          admin={isAdmin}
        />
      )}
      <Group gap="xs">
        <Text>{certificate.type.name}</Text>
        {certificate.reference ? (
          <Text fw={700}>
            {t("traceability:certificates.reference", {
              reference: certificate.reference,
            })}
          </Text>
        ) : null}
        <Group gap="xs">
          <Badge color={badgeColors[certificate.status]}>
            {badgeStatus[certificate.status]}
          </Badge>
          <DocumentActionsCell
            documentName={certificate.type.name}
            documentId={certificate.documentId}
            fetchDownloadUrl={() =>
              fetchDocumentUrl(certificate.documentId, true)
            }
            fetchViewUrl={() => fetchDocumentUrl(certificate.documentId)}
            missingDocumentText={t("common:documentUpload.missingDocument")}
          />
        </Group>
      </Group>
    </Stack>
  )
}
